import type { ComponentPropsWithRef } from 'react';

import {
    AppleLogoIcon,
    ApplePayIcon,
    AppleWalletIcon,
    Button,
    GoogleLogoIcon,
    GooglePayIcon,
    GoogleWalletIcon,
    Icon,
} from '@/shared-ui';

type PlatformButtonVariant = 'primary' | 'secondary';

interface PlatformButtonBaseProps extends ComponentPropsWithRef<'button'> {
    platform: 'apple' | 'google';
    variant?: PlatformButtonVariant;
}

const Login = ({ platform, variant = 'primary', children, ref, ...props }: PlatformButtonBaseProps) => (
    <Button
        data-slot="platform-button"
        data-type="login"
        data-platform={platform}
        data-variant={variant}
        variant={variant}
        ref={ref}
        {...props}>
        <Icon icon={platform === 'apple' ? AppleLogoIcon : GoogleLogoIcon} decorative className="size-5" />
        {children}
    </Button>
);

const Payment = ({ platform, variant = 'primary', children, ref, ...props }: PlatformButtonBaseProps) => (
    <Button
        aria-label={platform === 'apple' ? 'Apple Pay' : 'Google Pay'}
        data-slot="platform-button"
        data-type="payment"
        data-platform={platform}
        data-variant={variant}
        variant={variant}
        ref={ref}
        {...props}>
        <Icon icon={platform === 'apple' ? ApplePayIcon : GooglePayIcon} decorative className="size-auto" />
        {children}
    </Button>
);

const Wallet = ({ platform, variant = 'primary', children, ref, ...props }: PlatformButtonBaseProps) => (
    <Button
        aria-label={platform === 'apple' ? 'Apple Wallet' : 'Google Wallet'}
        data-slot="platform-button"
        data-type="wallet"
        data-platform={platform}
        data-variant={variant}
        variant={variant}
        ref={ref}
        {...props}>
        <Icon icon={platform === 'apple' ? AppleWalletIcon : GoogleWalletIcon} decorative className="size-auto" />
        {children}
    </Button>
);

Login.displayName = 'PlatformButton.Login';
Payment.displayName = 'PlatformButton.Payment';
Wallet.displayName = 'PlatformButton.Wallet';

export const PlatformButton = { Login, Payment, Wallet } as const;
